import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageSkeleton from '../common/Skeleton';
import Pagination from '../common/Pagination';
import axios from 'axios';
import { Search, Trash2, Filter, Plus, ArrowUpDown, ArrowUp, ArrowDown, Copy, BarChart3 } from 'lucide-react';
import { Button, IconButton, DataTable, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, EmptyState, StatusBadge, SearchFilterBar } from '../common/ui';
import { formatDateTime } from '../../utils/dateUtils';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

const Repairs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState(() => {
    // Initialize from URL query params if present
    return searchParams.get('search') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    const statusFromUrl = searchParams.get('status');
    return statusFromUrl || getUserPreference('repairs', 'statusFilter', 'all');
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => parseInt(getUserPreference('repairs', 'itemsPerPage', '50')));
  const [statusCounts, setStatusCounts] = useState({});
  
  const [sortField, setSortField] = useState(() => {
    return searchParams.get('sortField') || getUserPreference('repairs', 'sortField', 'id');
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return searchParams.get('sortDirection') || getUserPreference('repairs', 'sortDirection', 'desc');
  });

  useEffect(() => {
    const statusFromUrl = searchParams.get('status') || getUserPreference('repairs', 'statusFilter', 'all');
    const searchFromUrl = searchParams.get('search') || '';
    const sortFieldFromUrl = searchParams.get('sortField') || getUserPreference('repairs', 'sortField', 'id');
    const sortDirectionFromUrl = searchParams.get('sortDirection') || getUserPreference('repairs', 'sortDirection', 'desc');
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    
    setCurrentPage(pageFromUrl);
    fetchData(pageFromUrl, itemsPerPage, searchFromUrl, statusFromUrl, sortFieldFromUrl, sortDirectionFromUrl);
    fetchStatusCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to URL parameter changes (for browser back/forward)
  useEffect(() => {
    const statusFromUrl = searchParams.get('status') || getUserPreference('repairs', 'statusFilter', 'all');
    const searchFromUrl = searchParams.get('search') || '';
    const sortFieldFromUrl = searchParams.get('sortField') || getUserPreference('repairs', 'sortField', 'id');
    const sortDirectionFromUrl = searchParams.get('sortDirection') || getUserPreference('repairs', 'sortDirection', 'desc');
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    
    // Only update if values actually changed
    if (statusFromUrl !== statusFilter || searchFromUrl !== searchTerm || 
        sortFieldFromUrl !== sortField || sortDirectionFromUrl !== sortDirection ||
        pageFromUrl !== currentPage) {
      setStatusFilter(statusFromUrl);
      setSearchTerm(searchFromUrl);
      setSortField(sortFieldFromUrl);
      setSortDirection(sortDirectionFromUrl);
      setCurrentPage(pageFromUrl);
      fetchData(pageFromUrl, itemsPerPage, searchFromUrl, statusFromUrl, sortFieldFromUrl, sortDirectionFromUrl);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (page = 1, limit = itemsPerPage, search = searchTerm, status = statusFilter, sort = sortField, direction = sortDirection) => {
    try {
      setLoading(true);
      
      // Build repairs API params
      const repairsParams = new URLSearchParams();
      repairsParams.set('page', page.toString());
      repairsParams.set('limit', limit.toString());
      if (search) repairsParams.set('search', search);
      if (status && status !== 'all' && !search) repairsParams.set('status', status);
      // Send sort params to server for proper pagination
      if (sort) repairsParams.set('sortField', sort);
      if (direction) repairsParams.set('sortDirection', direction);
      
      const repairsRes = await axios.get(`/api/repairs?${repairsParams.toString()}`);
      
      // Handle repairs data
      const repairsData = repairsRes.data.repairs || repairsRes.data;
      if (Array.isArray(repairsData)) {
        setRepairs(repairsData);
        
        // Handle pagination metadata
        if (repairsRes.data.pagination) {
          setTotalPages(repairsRes.data.pagination.totalPages || 1);
          setTotalItems(repairsRes.data.pagination.totalItems || repairsData.length);
          setCurrentPage(repairsRes.data.pagination.currentPage || page);
        } else {
          // Fallback for backends that don't return pagination metadata
          setTotalPages(repairsData.length === limit ? page + 1 : page);
          setTotalItems(repairsData.length);
          setCurrentPage(page);
        }
      } else {
        setRepairs([]);
        setError('Invalid repairs data format received from server');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      // Fetch counts for each status
      const statuses = [
        'backlog',
        'waiting_for_repair',
        'clarification_required',
        'waiting_for_dev_input',
        'waiting_for_customer_response',
        'work_in_progress',
        'repaired',
        'ready_for_shipping',
        'waiting_for_payment',
        'completed'
      ];

      const counts = {};
      let totalCount = 0;

      // Fetch count for each status
      await Promise.all(statuses.map(async (status) => {
        try {
          const response = await axios.get(`/api/repairs?status=${status}&limit=1`);
          const count = response.data.pagination?.totalItems || response.data.length || 0;
          counts[status] = count;
          totalCount += count;
        } catch (error) {
          console.error(`Error fetching count for status ${status}:`, error);
          counts[status] = 0;
        }
      }));

      counts.all = totalCount;
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error fetching status counts:', error);
    }
  };

  const handleView = (repair) => {
    navigate(`/repairs/${repair.id}/view`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this repair?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/repairs/${id}`);
      setSuccess('Repair deleted successfully');
      fetchData();
      fetchStatusCounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting repair:', error);
      setError('Failed to delete repair');
    }
  };


  const runSearch = () => {
    setCurrentPage(1);
    // Update URL params
    updateUrlParams(searchTerm, statusFilter, sortField, sortDirection, 1);
    fetchData(1);
  };

  // Helper function to update URL parameters
  const updateUrlParams = (search, status, sort, direction, page = 1) => {
    const newParams = new URLSearchParams();
    
    // Only add non-default values to keep URLs clean
    if (search && search.trim()) {
      newParams.set('search', search.trim());
    }
    if (status && status !== 'all') {
      newParams.set('status', status);
    }
    if (sort && sort !== 'id') {
      newParams.set('sortField', sort);
    }
    if (direction && direction !== 'desc') {
      newParams.set('sortDirection', direction);
    }
    if (page && page > 1) {
      newParams.set('page', page.toString());
    }
    
    setSearchParams(newParams);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setUserPreference('repairs', 'statusFilter', status);
    setCurrentPage(1);
    
    // Update URL params while preserving search and sort parameters
    updateUrlParams(searchTerm, status, sortField, sortDirection, 1);
    
    // Trigger search immediately with the new status value
    fetchData(1, itemsPerPage, searchTerm, status);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL params to track page in browser history
    updateUrlParams(searchTerm, statusFilter, sortField, sortDirection, page);
    fetchData(page);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setUserPreference('repairs', 'itemsPerPage', newLimit);
    setCurrentPage(1);
    // Update URL params (reset to page 1)
    updateUrlParams(searchTerm, statusFilter, sortField, sortDirection, 1);
    fetchData(1, newLimit);
  };

  // Handle column sort
  const handleSort = (field) => {
    let newDirection = 'asc';
    
    // If clicking the same field, toggle direction
    if (field === sortField) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // If clicking a new field, default to desc for id, desc for updated_at (most recent first)
      newDirection = (field === 'id' || field === 'updated_at' || field === 'reported_date') ? 'desc' : 'asc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    setUserPreference('repairs', 'sortField', field);
    setUserPreference('repairs', 'sortDirection', newDirection);
    
    // Update URL params while preserving search and status filter (reset to page 1)
    updateUrlParams(searchTerm, statusFilter, field, newDirection, 1);
    
    // Fetch data with new sort params (reset to page 1)
    setCurrentPage(1);
    fetchData(1, itemsPerPage, searchTerm, statusFilter, field, newDirection);
  };

  const isTestEnv = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test';
  const isDesktop = isTestEnv ? true : (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 768px)').matches : true);

  return (
    <div className="px-4 pt-0 pb-20">
      {/* Header - Responsive Layout */}
      <div className="mb-2">
        {/* Render only one layout in tests to avoid duplicates */}
        {(!isDesktop) && (
          <div className="block md:hidden space-y-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Repairs</h1>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/repairs/new')}
                icon={Plus}
                className="w-full sm:w-auto"
              >
                New Repair
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/repairs/report')}
                icon={BarChart3}
                className="w-full sm:w-auto"
              >
                Module Report
              </Button>
            </div>
          </div>
        )}
        {(isDesktop) && (
          <div className="hidden md:flex md:justify-between md:items-center">
            <h1 className="text-2xl font-bold text-gray-900">Repairs</h1>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/repairs/report')}
                icon={BarChart3}
              >
                Module Report
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/repairs/new')}
                icon={Plus}
              >
                New Repair
              </Button>
            </div>
          </div>
        )}
      </div>

      <SearchFilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by serial, device model, or customer..."
        useDeviceAutocomplete={true}
        filters={[
          {
            key: 'status',
            value: statusFilter,
            options: [
              { value: 'all', label: 'All statuses' },
              { value: 'backlog', label: 'Backlog' },
              { value: 'waiting_for_repair', label: 'Waiting for repair' },
              { value: 'clarification_required', label: 'Clarification required' },
              { value: 'waiting_for_dev_input', label: 'Waiting for dev input' },
              { value: 'waiting_for_customer_response', label: 'Waiting for customer response' },
              { value: 'work_in_progress', label: 'Work in progress' },
              { value: 'repaired', label: 'Repaired' },
              { value: 'waiting_for_payment', label: 'Waiting for payment' },
              { value: 'ready_for_shipping', label: 'Ready for shipping' },
              { value: 'completed', label: 'Completed' }
            ]
          }
        ]}
        onFilterChange={(key, value) => {
          if (key === 'status') {
            handleStatusFilterChange(value);
          }
        }}
        onSearch={runSearch}
        onSearchSubmit={runSearch}
        onSearchClear={() => {
          setSearchTerm('');
          setCurrentPage(1);
          updateUrlParams('', statusFilter, sortField, sortDirection, 1);
          fetchData(1, itemsPerPage, '', statusFilter);
        }}
      />

      {/* Status Filter Buttons */}
      <div className="mb-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleStatusFilterChange('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-naviter-blue text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.all || 0}
            </span>
          </button>
          
          <button
            onClick={() => handleStatusFilterChange('backlog')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'backlog'
                ? 'bg-slate-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Backlog
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.backlog || 0}
            </span>
          </button>
          
          <button
            onClick={() => handleStatusFilterChange('waiting_for_repair')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'waiting_for_repair'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Waiting for Repair
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.waiting_for_repair || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('clarification_required')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'clarification_required'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Clarification Required
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.clarification_required || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('waiting_for_dev_input')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'waiting_for_dev_input'
                ? 'bg-cyan-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Waiting for Dev Input
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.waiting_for_dev_input || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('waiting_for_customer_response')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'waiting_for_customer_response'
                ? 'bg-amber-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Waiting for Customer Response
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.waiting_for_customer_response || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('work_in_progress')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'work_in_progress'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Work in Progress
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.work_in_progress || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('repaired')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'repaired'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Repaired
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.repaired || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('waiting_for_payment')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'waiting_for_payment'
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Waiting for Payment
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.waiting_for_payment || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('ready_for_shipping')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'ready_for_shipping'
                ? 'bg-purple-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Ready for Shipping
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.ready_for_shipping || 0}
            </span>
          </button>

          <button
            onClick={() => handleStatusFilterChange('completed')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'completed'
                ? 'bg-gray-800 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Completed
            <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
              {statusCounts.completed || 0}
            </span>
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Top Pagination */}
      {!loading && (
      <Pagination
        position="top"
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      )}

      {/* Repairs Table */}
      {loading ? (
        <PageSkeleton rows={10} rowHeight="h-16" />
      ) : (
      <DataTable
        emptyState={repairs.length === 0 ? (
          <EmptyState message="No repairs found" />
        ) : null}
      >
        <TableHeader>
          <tr>
            <TableHeaderCell 
              className="whitespace-nowrap cursor-pointer select-none hover:bg-gray-50"
              onClick={() => handleSort('id')}
            >
              <div className="flex items-center gap-2">
                <span>Repair #</span>
                {sortField === 'id' ? (
                  sortDirection === 'asc' ? (
                    <ArrowUp className="w-4 h-4 text-naviter-blue" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-naviter-blue" />
                  )
                ) : (
                  <ArrowUpDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </TableHeaderCell>
            <TableHeaderCell className="whitespace-nowrap">Device</TableHeaderCell>
            <TableHeaderCell>Notes</TableHeaderCell>
            <TableHeaderCell className="text-center">Status</TableHeaderCell>
            <TableHeaderCell 
              className="cursor-pointer select-none hover:bg-gray-50"
              onClick={() => handleSort('reported_date')}
            >
              <div className="flex items-center gap-2">
                <span>Created</span>
                {sortField === 'reported_date' ? (
                  sortDirection === 'asc' ? (
                    <ArrowUp className="w-4 h-4 text-naviter-blue" />
                  ) : (
                    <ArrowDown className="w-4 h-4 text-naviter-blue" />
                  )
                ) : (
                  <ArrowUpDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </TableHeaderCell>
            <TableHeaderCell className="text-center">Actions</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {repairs.map((repair) => (
            <TableRow 
              key={repair.id} 
              clickable={true}
              href={`/repairs/${repair.id}/view`}
              onClick={() => handleView(repair)}
            >
              <TableCell className="whitespace-nowrap group">
                <div className="flex items-center gap-1.5">
                  <div className="text-xs font-medium text-gray-900">
                    #{repair.id}
                  </div>
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 focus:opacity-100 focus:outline-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(repair.id.toString());
                    }}
                    title="Copy repair number"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="text-xs font-medium text-gray-900">
                  {repair.device_model || 'Unknown model'}
                </div>
                <div className="text-xs text-gray-500">
                  {repair.device_serial || 'N/A'}
                </div>
              </TableCell>
              <TableCell className="max-w-xs">
                <div className="text-xs font-medium text-gray-900 truncate">
                  {repair.customer_name || 'N/A'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {repair.final_resolution || repair.acceptance_notes}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col items-center">
                  <StatusBadge status={repair.status} variant="repair" />
                  <div className="text-xs text-gray-500 mt-0.5">
                    {repair.last_status_updater || 'Unknown'}
                  </div>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="text-xs text-gray-900">
                  {formatDateTime(repair.reported_date)}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Updated: {formatDateTime(repair.updated_at || repair.reported_date)}
                </div>
              </TableCell>
              <TableCell className="text-sm font-medium">
                <div className="flex justify-center">
                  <IconButton
                    variant="delete"
                    icon={Trash2}
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(repair.id); }}
                    title="Delete repair"
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
      )}

      {/* Pagination */}
      {!loading && (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
      )}
    </div>
  );
};

export default Repairs;